import { ListBuilder } from '../builder/list-item.builder';
import { isHeaderItem, isValueItem } from '../list.helpers';
import {
  EnforcedGroupListItems,
  EnforcedListItems,
  GroupListItem,
  ListItem,
  ListReservedValues,
  ListValue,
  StatefulList,
  ValueListItem,
} from '../stateful-list.types';

type Matcher = (label: string) => boolean;
export const NO_RESULT_ITEM = {
  label: 'No result',
  focusId: '',
  value: ListReservedValues.noResult,
};

type CreateNoResultListProps = {
  id?: string;
  label?: string;
};
export function createNoResultList({
  id,
  label,
}: CreateNoResultListProps = {}): StatefulList {
  return ListBuilder.create(id)
    .addGroup((group) => group.addItem(ListReservedValues.noResult, label ?? 'No result'))
    .build();
}

/**
 * Factory function that creates a list filter based on the given search term.
 *
 * @param searchTerm - The input text used to filter list item labels.
 * @returns An object with a `filter` method to apply filtering to a list.
 */
export function createListFilter(searchTerm: string) {
  const matcher = createMatcher(searchTerm);

  /**
   * Filters the given list based on the search term.
   *
   * @param list - The full list of groups to filter.
   * @returns A new list containing only groups and items that match the search criteria.
   */
  function filter(list: StatefulList): StatefulList {
    if (searchTerm.trim() === '') return list;
    // Filter groups: each group's items are filtered independently.
    const groups = list.groups
      .map((group) => filterGroup(group, matcher))
      .filter((group) => group !== null) as EnforcedGroupListItems;

    const valueItemMap = new Map<ListValue, ValueListItem>();
    groups.forEach((group) => {
      group.valueItemMap.forEach((item, key) => {
        valueItemMap.set(key, item);
      });
    });

    // When it results in no groups, return a single group with a "No result" item.
    return groups.length === 0
      ? createNoResultList({ id: list.id })
      : { id: list.id, groups, valueItemMap };
  }

  return { filter };
}

/**
 * Creates a matching function based on space-separated lowercase terms.
 *
 * @param term - The raw search input.
 * @returns A function that returns `true` if all terms are found in a label.
 */
function createMatcher(term: string): Matcher {
  const terms = term.trim().toLowerCase().split(/\s+/);
  return (label) => terms.every((t) => label.toLowerCase().includes(t));
}

/**
 * Filters a single group based on the matcher.
 *
 * @param group - The group to filter.
 * @param matches - The matcher function to check label matches.
 * @returns Filtered group or null if no items matched.
 */
function filterGroup(group: GroupListItem, matches: Matcher): GroupListItem | null {
  const filteredItems = group.items.filter((item, index) => {
    if (isValueItem(item)) return matches(item.label as string);
    if (isHeaderItem(item)) return shouldIncludeHeader(group.items, index, matches);
    return false;
  });

  if (filteredItems.length === 0) return null;

  const map = filteredItems.filter(isValueItem).reduce((acc, item) => {
    acc.set(item.value, item);
    return acc;
  }, new Map<ListValue, ValueListItem>());

  // filteredItems is at least one element due to length check
  return {
    valueItemMap: map,
    items: filteredItems as EnforcedListItems,
  };
}

/**
 * Determines whether a header should be kept based on upcoming content.
 *
 * @param items - The list of items within the group.
 * @param index - The index of the header in the items.
 * @param matches - The matcher function for label matching.
 * @returns `true` if the next items contain at least one matching value item.
 */
function shouldIncludeHeader(
  items: ListItem[],
  index: number,
  matches: Matcher
): boolean {
  // Look ahead to next items until next header or end
  for (let i = index + 1; i < items.length; i++) {
    const next = items[i];
    if (isHeaderItem(next)) break;
    if (isValueItem(next) && matches(next.label as string)) return true;
  }
  return false;
}
